import { api, type ApiResponse } from './api'
import { descargar } from './descarga'

/**
 * Cliente del directorio de clientes.
 *
 * A diferencia del catálogo, aquí no hay lectura unificada con otra entidad: el
 * directorio es una sola tabla, así que un endpoint por entidad es lo más
 * simple que puede ser.
 */

export interface Customer {
  id: number
  name: string
  /** Identificación fiscal. `null` cuando el cliente aún no tiene. */
  document: string | null
  email: string | null
  phone: string | null
  company: string | null
  city: string | null
  address: string | null
  source: string | null
  created_at: string | null
  updated_at: string | null
}

/** Proyección del autocompletado: sin `company` ni `address` porque no se muestran. */
export interface CustomerSuggestion {
  id: number
  name: string
  document: string | null
  email: string | null
  phone: string | null
  /**
   * Ciudad y origen.
   *
   * El autocompletado los trae porque el cotizador los necesita: al elegir un cliente de
   * la lista, el comercial espera ver su ciudad en vez de teclearla otra vez. Y al
   * guardarla llegan al directorio, que es donde se busca al mes siguiente.
   */
  city: string | null
  source: string | null
}

export interface CustomerMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  query: Record<string, unknown>
}

export interface CustomerPage {
  data: Customer[]
  meta: CustomerMeta
}

/** Payload de escritura. `document` es `null` cuando el cliente no tiene NIT. */
export interface CustomerPayload {
  name: string
  document?: string | null
  email?: string | null
  phone?: string | null
  company?: string | null
  city?: string | null
  address?: string | null
  source?: string | null
}

export interface CustomerListParams {
  search?: string
  city?: string
  page?: number
  per_page?: number
}

/** Cotización tal y como la devuelve `QuoteResource`. */
export interface CustomerQuote {
  id: number
  quote_number: string | null
  customer_id: number | null
  customer_name: string
  document: string | null
  user_id: number | null
  user_name: string | null
  quote_date: string | null
  price_type: string
  status: string
  total_price: string
  is_invoiced: boolean
  invoice_number: string | null
  unread_notes_count: number
  next_follow_up_date: string | null
  last_follow_up_note: string | null
}

export interface CustomerQuoteMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
  query: { date_from: string | null; date_to: string | null }
}

export interface CustomerQuotePage {
  data: CustomerQuote[]
  meta: CustomerQuoteMeta
}

/**
 * Filtros de las cotizaciones de un cliente.
 *
 * Las dos fechas son opcionales y no tienen valor por defecto a propósito: el
 * backend devuelve todas las cotizaciones cuando no llegan. Es lo que permite que
 * entrar a la ficha desde el listado de clientes enseñe el historial completo,
 * en vez de un mes vacío sin explicación.
 */
export interface CustomerQuoteListParams {
  date_from?: string
  date_to?: string
  page?: number
  per_page?: number
}

/** Ciudades para el filtro del listado. */
export const CUSTOMER_SOURCES = ['web', 'referido', 'redes', 'visita'] as const

export type CustomerSource = (typeof CUSTOMER_SOURCES)[number]

/**
 * Descarta los filtros vacíos antes de la petición.
 *
 * `search: ''` llegaría al backend como parámetro presente, y el listado
 * filtraría por el string vacío en vez de devolver el directorio completo.
 */
function withoutEmpty<T extends object>(params: T): Partial<T> {
  const cleaned: Partial<T> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    cleaned[key as keyof T] = value as T[keyof T]
  }
  return cleaned
}

export const customerApi = {
  list(params: CustomerListParams = {}) {
    return api.get<CustomerPage>('/customers', { params: withoutEmpty(params) })
  },

  get(id: number) {
    return api.get<ApiResponse<Customer>>(`/customers/${id}`)
  },

  /**
   * Cotizaciones de un cliente.
   *
   * El permiso es `quotes.manage` y no `customers.manage`: son cotizaciones, con
   * su importe y su estado, y esos datos los protege el módulo de cotizaciones.
   */
  quotes(id: number, params: CustomerQuoteListParams = {}) {
    return api.get<CustomerQuotePage>(`/customers/${id}/quotes`, {
      params: withoutEmpty(params),
    })
  },

  create(payload: CustomerPayload) {
    return api.post<ApiResponse<Customer>>('/customers', payload)
  },

  update(id: number, payload: Partial<CustomerPayload>) {
    return api.patch<ApiResponse<Customer>>(`/customers/${id}`, payload)
  },

  remove(id: number) {
    return api.delete(`/customers/${id}`)
  },

  /**
   * Autocompletado para el cotizador. El backend limita a 5.
   *
   * Se manda siempre un `q` no vacío: el endpoint responde 422 sin él, porque
   * sin término devolvería el directorio entero y saturaría el desplegable.
   */
  search(q: string) {
    const term = q.trim()
    return api.get<ApiResponse<CustomerSuggestion[]>>('/customers/search', { params: { q: term } })
  },

  /**
   * Descarga el directorio completo de clientes como `.xlsx`.
   *
   * Respeta el filtro de búsqueda activo: si el usuario está filtrando por un
   * nombre, el fichero trae solo los que coinciden, que es lo que espera ver
   * al pulsar el botón. Sin filtro, trae la base entera.
   */
  async export(params: CustomerListParams = {}): Promise<void> {
    await descargar('/customers/export', params as Record<string, unknown>)
  },
}
